// api.js
import axios from "axios";

// Determine API base URL
// This is set at BUILD TIME via REACT_APP_API_BASE_URL environment variable
// - Localhost/Docker: Set to http://localhost:5000 in docker-compose.yml
// - AWS: Set to the AWS backend URL in deploy-multistage.sh
const getApiBase = () => {
  // Check if we have an explicit environment variable (set during build)
  if (process.env.REACT_APP_API_BASE_URL) {
    const apiBase = `${process.env.REACT_APP_API_BASE_URL}/api`;
    console.log(`API Base URL: ${apiBase} (from REACT_APP_API_BASE_URL)`);
    return apiBase;
  }

  // Dev: same-origin /api via CRA proxy (package.json) — avoids CORS when using LAN IP or localhost
  if (process.env.NODE_ENV === "development") {
    return "/api";
  }

  // Production fallback when REACT_APP_API_BASE_URL wasn't set during build
  console.warn("REACT_APP_API_BASE_URL not set, using localhost:5000 fallback");
  return "http://localhost:5000/api";
};

export const API_BASE = getApiBase();

// Session-expired handler — set by AuthContext so the interceptor can
// trigger a clean React logout without importing React in this module.
let _sessionExpiredHandler = null;
export function setSessionExpiredHandler(fn) {
  _sessionExpiredHandler = fn;
}

// Default timeout: 30 seconds for most requests, 60 seconds for other long calls
const DEFAULT_TIMEOUT = 30000; // 30 seconds
const LONG_TIMEOUT = 60000; // 60 seconds (generic long operations)

// Query search: RAG + LLM often exceeds 60s (Docker, large context). If the client times out first,
// Axios aborts but the server keeps running and saves the answer — reload then shows it.
// Override at build time: REACT_APP_QUERY_SEARCH_TIMEOUT_MS=240000
const QUERY_SEARCH_TIMEOUT_MS = Math.max(
  60000,
  parseInt(process.env.REACT_APP_QUERY_SEARCH_TIMEOUT_MS || "180000", 10) || 180000
);

// Standardized error shape
const normalizeError = (error) => {
  // If error is already normalized, return as-is
  if (error && error.isNormalized) {
    return error;
  }

  const normalized = {
    isNormalized: true,
    message: "An unexpected error occurred",
    code: null,
    status: null,
    details: null,
    requestId: null,
    retryable: false,
    originalError: error,
  };

  if (axios.isAxiosError(error)) {
    // Network error (no response received)
    if (!error.response) {
      if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
        normalized.message = "Request timeout: The server took too long to respond";
        normalized.code = "TIMEOUT";
        normalized.retryable = true;
      } else if (error.code === "ERR_NETWORK" || error.message.includes("Network Error")) {
        normalized.message = "Network error: Unable to connect to server. Please check your internet connection.";
        normalized.code = "NETWORK_ERROR";
        normalized.retryable = true;
      } else {
        normalized.message = error.message || "Network error occurred";
        normalized.code = error.code || "NETWORK_ERROR";
        normalized.retryable = true;
      }
      return normalized;
    }

    // HTTP error response
    const response = error.response;
    normalized.status = response.status;
    normalized.requestId = response.headers["x-request-id"] || null;

    // Extract error message from response
    if (response.data) {
      if (typeof response.data === "string") {
        normalized.message = response.data;
      } else if (response.data.error) {
        normalized.message = response.data.error;
        normalized.details = response.data.details || null;
      } else if (response.data.message) {
        normalized.message = response.data.message;
      }
      // Preserve stable server codes / readiness payloads (e.g. INTENT_CONTRACT_NOT_READY).
      if (typeof response.data.code === "string" && response.data.code) {
        normalized.code = response.data.code;
      }
      if (typeof response.data.outcome === "string" && response.data.outcome) {
        normalized.outcome = response.data.outcome;
      }
      if (response.data.receipt != null) {
        normalized.receipt = response.data.receipt;
      }
      if (response.data.snapshot != null) {
        normalized.snapshot = response.data.snapshot;
      }
      if (response.data.readiness != null) {
        normalized.readiness = response.data.readiness;
      }
    }

    // Map HTTP status codes to error codes and retryability (unless server sent a stable code).
    if (!normalized.code) {
      switch (response.status) {
        case 400:
          normalized.code = "BAD_REQUEST";
          normalized.retryable = false;
          break;
        case 401:
          normalized.code = "UNAUTHORIZED";
          normalized.retryable = false;
          normalized.message = "Authentication failed. Please log in again.";
          break;
        case 403:
          normalized.code = "FORBIDDEN";
          normalized.retryable = false;
          normalized.message = normalized.message || "Access denied";
          break;
        case 404:
          normalized.code = "NOT_FOUND";
          normalized.retryable = false;
          normalized.message = normalized.message || "Resource not found";
          break;
        case 429:
          normalized.code = "RATE_LIMITED";
          normalized.retryable = true;
          // Keep server text when it explains the wait (e.g. auth: 15 min lockout)
          if (!normalized.message || normalized.message === "Rate limit exceeded") {
            normalized.message =
              "Rate limit exceeded. Please wait a moment and try again.";
          }
          break;
        case 500:
        case 502:
        case 503:
        case 504:
          normalized.code = "SERVER_ERROR";
          normalized.retryable = true;
          normalized.message = normalized.message || "Server error. Please try again later.";
          break;
        default:
          normalized.code = `HTTP_${response.status}`;
          normalized.retryable = response.status >= 500;
      }
    } else if (response.status === 401) {
      normalized.retryable = false;
      normalized.message = "Authentication failed. Please log in again.";
    } else if (response.status === 429) {
      normalized.retryable = true;
    } else if (response.status >= 500) {
      normalized.retryable = true;
    } else {
      normalized.retryable = false;
    }
  } else if (error instanceof Error) {
    // Generic JavaScript error
    normalized.message = error.message;
    normalized.code = error.name || "ERROR";
  }

  return normalized;
};

// Retry helper with exponential backoff
const retryRequest = async (requestFn, maxRetries = 3, baseDelay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      const normalized = normalizeError(error);
      
      // Don't retry if error is not retryable or we've exhausted retries
      if (!normalized.retryable || attempt === maxRetries) {
        throw normalized;
      }
      
      // Calculate exponential backoff delay
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  
  throw normalizeError(lastError);
};

const ACTIVE_ORG_HEADER = "X-Organization-Id";
let _activeOrganizationId = null;

export function setActiveOrganizationId(organizationId) {
  _activeOrganizationId =
    organizationId != null && String(organizationId).trim()
      ? String(organizationId).trim()
      : null;
}

export function getActiveOrganizationId() {
  return _activeOrganizationId;
}

function isPublicAuthRoute(url = "") {
  const path = String(url).split("?")[0];
  return (
    path.startsWith("/auth/") ||
    path === "/organization-invites/inspect" ||
    path.startsWith("/organization-invites/inspect")
  );
}

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  timeout: DEFAULT_TIMEOUT,
});

// Add token and workspace header to authenticated API requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  if (_activeOrganizationId && !isPublicAuthRoute(config.url)) {
    config.headers[ACTIVE_ORG_HEADER] = _activeOrganizationId;
  }
  return config;
});

// Response interceptor: normalize errors and handle 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401: clear auth and notify via session-expired handler (no reload)
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      if (_sessionExpiredHandler) {
        _sessionExpiredHandler();
      }
    }
    
    // Normalize error before rejecting
    return Promise.reject(normalizeError(error));
  }
);

// Helper function for fetching repository metadata
api.getRepoMetadata = (repoId) => api.get(`/repos/${repoId}/metadata`);

// Notes API helpers
api.getNotes = (repoId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.filePath !== undefined) {
    queryParams.append('filePath', params.filePath === null ? 'null' : params.filePath);
  }
  const queryString = queryParams.toString();
  const url = `/repos/${repoId}/notes${queryString ? `?${queryString}` : ''}`;
  return api.get(url);
};

api.createNote = (repoId, data) => api.post(`/repos/${repoId}/notes`, data);

api.updateNote = (repoId, noteId, data) => api.put(`/repos/${repoId}/notes/${noteId}`, data);

api.deleteNote = (repoId, noteId) => api.delete(`/repos/${repoId}/notes/${noteId}`);

// Codebase Guide (Repo Playbook) helpers
api.listCodebaseGuide = (repoId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.activeOnly) queryParams.append("activeOnly", "true");
  if (params.category) queryParams.append("category", params.category);
  const queryString = queryParams.toString();
  return api.get(
    `/repos/${repoId}/codebase-guide${queryString ? `?${queryString}` : ""}`
  );
};

api.createCodebaseGuideEntry = (repoId, data) =>
  api.post(`/repos/${repoId}/codebase-guide`, data);

api.updateCodebaseGuideEntry = (repoId, entryId, data) =>
  api.patch(`/repos/${repoId}/codebase-guide/${entryId}`, data);

api.deleteCodebaseGuideEntry = (repoId, noteId) =>
  api.delete(`/repos/${repoId}/codebase-guide/${noteId}`);

api.listGuides = (repoId, params = {}) => api.get(`/repos/${repoId}/guides`, { params });
api.createGuide = (repoId, data) => api.post(`/repos/${repoId}/guides`, data);
api.getGuide = (repoId, guideId) => api.get(`/repos/${repoId}/guides/${guideId}`);
api.updateGuide = (repoId, guideId, data) => api.patch(`/repos/${repoId}/guides/${guideId}`, data);
api.verifyGuide = (repoId, guideId) => api.post(`/repos/${repoId}/guides/${guideId}/verify`);
api.deactivateGuide = (repoId, guideId) => api.post(`/repos/${repoId}/guides/${guideId}/deactivate`);
api.guideHistory = (repoId, guideId) => api.get(`/repos/${repoId}/guides/${guideId}/history`);
api.listGuideSuggestions = (repoId, params = {}) => api.get(`/repos/${repoId}/guide-suggestions`, { params });
api.createGuideSuggestion = (repoId, data) => api.post(`/repos/${repoId}/guide-suggestions`, data);
api.acceptGuideSuggestion = (repoId, suggestionId, data) =>
  api.post(`/repos/${repoId}/guide-suggestions/${suggestionId}/accept`, data);
api.rejectGuideSuggestion = (repoId, suggestionId, data) =>
  api.post(`/repos/${repoId}/guide-suggestions/${suggestionId}/reject`, data);
api.listGuideConflicts = (repoId, params = {}) => api.get(`/repos/${repoId}/guide-conflicts`, { params });
api.resolveGuideConflict = (repoId, conflictId, data) =>
  api.post(`/repos/${repoId}/guide-conflicts/${conflictId}/resolve`, data);

// Repo Roadmap helpers
api.getRepoRoadmap = (repoId) => api.get(`/repos/${repoId}/roadmap`);

api.updateRepoRoadmap = (repoId, data) =>
  api.patch(`/repos/${repoId}/roadmap`, data);

api.createRoadmapItem = (repoId, data) =>
  api.post(`/repos/${repoId}/roadmap/items`, data);

api.updateRoadmapItem = (repoId, itemId, data) =>
  api.patch(`/repos/${repoId}/roadmap/items/${itemId}`, data);

api.deleteRoadmapItem = (repoId, itemId) =>
  api.delete(`/repos/${repoId}/roadmap/items/${itemId}`);

api.linkRoadmapItemWorkBrief = (repoId, itemId, workBriefId, options = {}) =>
  api.post(`/repos/${repoId}/roadmap/items/${itemId}/link-work-brief`, {
    workBriefId,
    ...options,
  });

api.unlinkRoadmapItemWorkBrief = (repoId, itemId) =>
  api.post(`/repos/${repoId}/roadmap/items/${itemId}/unlink-work-brief`);

api.createWorkBriefFromRoadmapItem = (repoId, itemId, data = {}) =>
  api.post(`/repos/${repoId}/roadmap/items/${itemId}/create-work-brief`, data);

api.getPriorityCenter = (repoId) => api.get(`/repos/${repoId}/roadmap/priority-center`);

/** @deprecated No longer mass-imports Tasks. Prefer addPriorityCenterItems. */
api.ensurePriorityCenter = (repoId) =>
  api.post(`/repos/${repoId}/roadmap/priority-center/ensure`);

api.addPriorityCenterItems = (repoId, data) =>
  api.post(`/repos/${repoId}/roadmap/priority-center/items`, data);

api.removePriorityCenterItem = (repoId, featurePlanId, data) =>
  api.post(`/repos/${repoId}/roadmap/priority-center/items/${featurePlanId}/remove`, data);

api.reconcilePriorityCenterDuplicates = (repoId, data) =>
  api.post(`/repos/${repoId}/roadmap/priority-center/reconcile-duplicates`, data);

api.savePriorityCenter = (repoId, data) =>
  api.patch(`/repos/${repoId}/roadmap/priority-center`, data);

api.suggestPriorityCenter = (repoId) =>
  api.post(`/repos/${repoId}/roadmap/priority-center/suggest`);

api.getPrioritySprintCandidates = (repoId) =>
  api.post(`/repos/${repoId}/roadmap/priority-center/sprint-candidates`);

api.listSprintDesigns = (repoId) => api.get(`/repos/${repoId}/sprint-designs`);

api.createSprintDesign = (repoId, data) => api.post(`/repos/${repoId}/sprint-designs`, data);

api.getSprintDesign = (repoId, sprintId, version) =>
  api.get(`/repos/${repoId}/sprint-designs/${sprintId}/versions/${version}`);

api.updateSprintDesign = (repoId, sprintId, version, data) =>
  api.patch(`/repos/${repoId}/sprint-designs/${sprintId}/versions/${version}`, data);

api.recommendSprintDesign = (repoId, sprintId, version, data) =>
  api.post(`/repos/${repoId}/sprint-designs/${sprintId}/versions/${version}/recommendation`, data);

api.previewSprintCommit = (repoId, sprintId, version, data) =>
  api.post(`/repos/${repoId}/sprint-designs/${sprintId}/versions/${version}/preview-commit`, data);

api.commitSprintDesign = (repoId, sprintId, version, data) =>
  api.post(`/repos/${repoId}/sprint-designs/${sprintId}/versions/${version}/commit`, data);

api.reviseSprintDesign = (repoId, sprintId, version, data = {}) =>
  api.post(`/repos/${repoId}/sprint-designs/${sprintId}/versions/${version}/revisions`, data);

api.authorizeAgentWork = (repoId, data) =>
  api.post(`/repos/${repoId}/agent-work-orders`, data);

api.revokeAgentWork = (repoId, workOrderId) =>
  api.post(`/repos/${repoId}/agent-work-orders/${workOrderId}/revoke`);

api.answerAgentQuestion = (repoId, attemptId, data) =>
  api.post(`/repos/${repoId}/agent-attempts/${attemptId}/answer`, data);

api.requestAgentStop = (repoId, attemptId) =>
  api.post(`/repos/${repoId}/agent-attempts/${attemptId}/request-stop`);

api.listAgentPrincipals = (repoId) => api.get(`/repos/${repoId}/agent-principals`);

api.createAgentPrincipal = (repoId, data) =>
  api.post(`/repos/${repoId}/agent-principals`, data);

// Enhanced request methods with timeout and retry support
api.getWithTimeout = (url, config = {}) => {
  return api.get(url, {
    ...config,
    timeout: config.timeout || DEFAULT_TIMEOUT,
  });
};

api.postWithTimeout = (url, data, config = {}) => {
  return api.post(url, data, {
    ...config,
    timeout: config.timeout || DEFAULT_TIMEOUT,
  });
};

api.putWithTimeout = (url, data, config = {}) => {
  return api.put(url, data, {
    ...config,
    timeout: config.timeout || DEFAULT_TIMEOUT,
  });
};

api.deleteWithTimeout = (url, config = {}) => {
  return api.delete(url, {
    ...config,
    timeout: config.timeout || DEFAULT_TIMEOUT,
  });
};

// Long-running operations (queries, repo processing) with extended timeout
api.postQuery = (repoId, query, config = {}) => {
  return api.post(`/queries/${repoId}/search`, { query }, {
    ...config,
    timeout: config.timeout || QUERY_SEARCH_TIMEOUT_MS,
  });
};

// Retry wrapper for critical operations
api.withRetry = (requestFn, maxRetries = 3) => {
  return retryRequest(requestFn, maxRetries);
};

// Slack app integration helpers
api.getSlackInstallUrl = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.orgId) queryParams.append("orgId", params.orgId);
  if (params.redirectAfterInstall) {
    queryParams.append("redirectAfterInstall", params.redirectAfterInstall);
  }
  const queryString = queryParams.toString();
  return api.get(`/slack/install-url${queryString ? `?${queryString}` : ""}`);
};

api.getSlackStatus = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.orgId) queryParams.append("orgId", params.orgId);
  const queryString = queryParams.toString();
  return api.get(`/slack/status${queryString ? `?${queryString}` : ""}`);
};

api.sendSlackTestNotification = (params = {}) =>
  api.post("/slack/test-notification", params.orgId ? { orgId: params.orgId } : {});

export async function getSlackInstallUrl(params = {}) {
  const response = await api.getSlackInstallUrl(params);
  return response.data;
}

export async function getSlackStatus(params = {}) {
  const response = await api.getSlackStatus(params);
  return response.data;
}

export async function sendSlackTestNotification(params = {}) {
  const response = await api.sendSlackTestNotification(params);
  return response.data;
}

/**
 * Compare two analyzed repositories and return structured comparison + evidence report.
 */
export async function compareRepos({
  leftRepoId,
  rightRepoId,
  leftAnalysisRunId,
  rightAnalysisRunId,
  comparisonGoal,
  focusAreas,
} = {}) {
  const payload = {
    leftRepoId,
    rightRepoId,
  };
  if (leftAnalysisRunId) payload.leftAnalysisRunId = leftAnalysisRunId;
  if (rightAnalysisRunId) payload.rightAnalysisRunId = rightAnalysisRunId;
  if (comparisonGoal?.trim()) payload.comparisonGoal = comparisonGoal.trim();
  if (focusAreas?.length) payload.focusAreas = focusAreas;

  const response = await api.post("/repo-comparisons", payload, {
    timeout: LONG_TIMEOUT,
  });
  return response.data;
}

/**
 * Ask a grounded follow-up question about a repo-to-repo comparison.
 */
export async function askRepoComparisonQuestion({
  leftRepoId,
  rightRepoId,
  leftAnalysisRunId,
  rightAnalysisRunId,
  comparisonGoal,
  focusAreas,
  question,
  comparisonId,
} = {}) {
  const payload = {
    leftRepoId,
    rightRepoId,
    question: question?.trim(),
  };
  if (leftAnalysisRunId) payload.leftAnalysisRunId = leftAnalysisRunId;
  if (rightAnalysisRunId) payload.rightAnalysisRunId = rightAnalysisRunId;
  if (comparisonGoal?.trim()) payload.comparisonGoal = comparisonGoal.trim();
  if (focusAreas?.length) payload.focusAreas = focusAreas;
  if (comparisonId) payload.comparisonId = comparisonId;

  const response = await api.post("/repo-comparisons/ask", payload, {
    timeout: LONG_TIMEOUT,
  });
  return response.data;
}

/**
 * List saved repo-to-repo comparisons for the authenticated user.
 */
export async function listRepoComparisons({
  limit,
  before,
  leftRepoId,
  rightRepoId,
} = {}) {
  const params = new URLSearchParams();
  if (limit != null) params.append("limit", String(limit));
  if (before) params.append("before", before);
  if (leftRepoId) params.append("leftRepoId", leftRepoId);
  if (rightRepoId) params.append("rightRepoId", rightRepoId);
  const queryString = params.toString();
  const response = await api.get(
    `/repo-comparisons${queryString ? `?${queryString}` : ""}`
  );
  return response.data;
}

/**
 * Load one saved comparison with full comparison and evidence report payloads.
 */
export async function getRepoComparison(comparisonId) {
  const response = await api.get(`/repo-comparisons/${comparisonId}`);
  return response.data;
}

/**
 * List saved Q&A records linked to a saved comparison.
 */
export async function getRepoComparisonQuestions(
  comparisonId,
  { limit, before } = {}
) {
  const params = new URLSearchParams();
  if (limit != null) params.append("limit", String(limit));
  if (before) params.append("before", before);
  const queryString = params.toString();
  const response = await api.get(
    `/repo-comparisons/${comparisonId}/questions${queryString ? `?${queryString}` : ""}`
  );
  return response.data;
}

/**
 * Delete a saved repo-to-repo comparison and its linked saved Q&A records.
 */
export async function deleteRepoComparison(comparisonId) {
  const response = await api.delete(`/repo-comparisons/${comparisonId}`);
  return response.data;
}

/**
 * List all branch analyses for a logical repository (same owner/name/orgId).
 */
export async function listBranchAnalyses(repoId) {
  const response = await api.get(`/repos/${repoId}/branch-analyses`);
  return response.data;
}

export async function preflightGithubPullRequest(body) {
  const response = await api.post("/repos/pr/preflight", body);
  return response.data;
}

export async function submitGithubPullRequestAnalysis(body) {
  const response = await api.post("/repos/pr", body);
  return response.data;
}

export async function saveDeliveredWork(featurePlanId, { sourceInput } = {}) {
  const response = await api.put(
    `/feature-plans/${featurePlanId}/delivered-work`,
    { sourceInput },
    { timeout: DEFAULT_TIMEOUT }
  );
  return response.data;
}

export async function preflightCompareSource(repoId, body) {
  const response = await api.post(`/repos/${repoId}/compare-sources/preflight`, body);
  return response.data;
}

export async function submitCompareSource(repoId, body) {
  const response = await api.post(`/repos/${repoId}/compare-sources`, body);
  return response.data;
}

export async function getRepo(repoId) {
  const response = await api.get(`/repos/${repoId}`);
  return response.data;
}

export async function deleteRepo(repoId) {
  const response = await api.delete(`/users/repos/${repoId}`);
  return response.data;
}

/**
 * List persisted analysis snapshots for a repo (compact summaries with source metadata).
 */
export async function listRepoSnapshots(repoId, { limit } = {}) {
  const params = new URLSearchParams();
  if (limit != null) params.append("limit", String(limit));
  const queryString = params.toString();
  const response = await api.get(
    `/repos/${repoId}/snapshots${queryString ? `?${queryString}` : ""}`
  );
  return response.data;
}

/**
 * Compare two persisted snapshots for the same repo by exact snapshot IDs.
 */
export async function compareRepoSnapshots({
  repoId,
  beforeSnapshotId,
  afterSnapshotId,
} = {}) {
  const response = await api.post(
    `/repos/${repoId}/snapshots/compare`,
    {
      beforeSnapshotId,
      afterSnapshotId,
    },
    { timeout: LONG_TIMEOUT }
  );
  return response.data;
}

/**
 * Compare two snapshots that may belong to different branch-analysis Repo docs.
 */
export async function compareCrossBranchSnapshots({
  canonicalRepoId,
  beforeRepoId,
  beforeSnapshotId,
  afterRepoId,
  afterSnapshotId,
} = {}) {
  const response = await api.post(
    `/repos/${canonicalRepoId}/snapshots/compare/cross-branch`,
    { beforeRepoId, beforeSnapshotId, afterRepoId, afterSnapshotId },
    { timeout: LONG_TIMEOUT }
  );
  return response.data;
}

/**
 * Create an acceptance review for a completed Feature Plan against a GitHub branch.
 */
export async function preflightAcceptanceReview(
  featurePlanId,
  { branchName, baseRef, headSha, baseSha, analysisRepoId } = {}
) {
  const response = await api.post(
    `/feature-plans/${featurePlanId}/acceptance-reviews/preflight`,
    {
      branchName,
      ...(baseRef ? { baseRef } : {}),
      ...(headSha ? { headSha } : {}),
      ...(baseSha ? { baseSha } : {}),
      ...(analysisRepoId ? { analysisRepoId } : {}),
    },
    { timeout: DEFAULT_TIMEOUT }
  );
  return response.data;
}

export async function createAcceptanceReview(
  featurePlanId,
  { branchName, baseRef, headSha, baseSha, analysisRepoId, force } = {}
) {
  const response = await api.post(
    `/feature-plans/${featurePlanId}/acceptance-reviews`,
    {
      branchName,
      ...(baseRef ? { baseRef } : {}),
      ...(headSha ? { headSha } : {}),
      ...(baseSha ? { baseSha } : {}),
      ...(analysisRepoId ? { analysisRepoId } : {}),
      ...(force ? { force: true } : {}),
    },
    { timeout: LONG_TIMEOUT }
  );
  return response.data;
}

/**
 * Where this initiative is, why, what is blocking it, and the highest-value next
 * action. Read-only: the server derives the whole snapshot from the authoritative
 * records on every call.
 */
export async function getInitiativeOrchestration(featurePlanId) {
  const response = await api.get(`/feature-plans/${featurePlanId}/orchestration`, {
    timeout: DEFAULT_TIMEOUT,
  });
  return response.data;
}

/**
 * Carry out a recommended orchestration action against the current records.
 * The server recomputes eligibility; a stale fingerprint cannot authorize anything.
 */
export async function executeInitiativeCommand(featurePlanId, body) {
  const response = await api.post(
    `/feature-plans/${featurePlanId}/orchestration/commands`,
    body,
    { timeout: LONG_TIMEOUT }
  );
  return response.data;
}

/**
 * List acceptance reviews for a Feature Plan (newest first, server-capped).
 */
export async function listAcceptanceReviews(featurePlanId) {
  const response = await api.get(`/feature-plans/${featurePlanId}/acceptance-reviews`);
  return response.data;
}

/**
 * Fetch a single acceptance review by id.
 */
export async function getAcceptanceReview(reviewId) {
  const response = await api.get(`/acceptance-reviews/${reviewId}`);
  return response.data;
}

/**
 * Record a product decision on an acceptance review (accept / conditional / reject).
 */
export async function decideAcceptanceReview(reviewId, { event, note, conditions } = {}) {
  const response = await api.post(
    `/acceptance-reviews/${reviewId}/decision`,
    {
      event,
      ...(note ? { note } : {}),
      ...(conditions?.length ? { conditions } : {}),
    },
    { timeout: DEFAULT_TIMEOUT }
  );
  return response.data;
}

export async function exportAcceptanceReview(reviewId) {
  const response = await api.get(`/acceptance-reviews/${reviewId}/export`, {
    timeout: DEFAULT_TIMEOUT,
  });
  return response.data;
}

export async function resolveAcceptanceReviewCondition(
  reviewId,
  conditionId,
  { resolution, note } = {}
) {
  const response = await api.post(
    `/acceptance-reviews/${reviewId}/conditions/${encodeURIComponent(conditionId)}/resolution`,
    {
      resolution,
      ...(note ? { note } : {}),
    },
    { timeout: DEFAULT_TIMEOUT }
  );
  return response.data;
}

export async function applyAcceptanceReviewLifecycle(reviewId, { event } = {}) {
  const response = await api.post(
    `/acceptance-reviews/${reviewId}/lifecycle`,
    { event },
    { timeout: LONG_TIMEOUT }
  );
  return response.data;
}

export async function createTaskSource({ repoId, originalFilename, declaredRole }) {
  const response = await api.post(`/repos/${repoId}/task-sources`, {
    originalFilename,
    declaredRole,
  });
  return response.data.artifact;
}

export async function uploadTaskSourceContent({ repoId, artifactId, bytes, replace = false }) {
  const response = await api.put(
    `/repos/${repoId}/task-sources/${artifactId}/content${replace ? "?replace=1" : ""}`,
    bytes,
    {
      headers: { "Content-Type": "application/octet-stream" },
      timeout: LONG_TIMEOUT,
    }
  );
  return response.data.artifact;
}

export async function listTaskSources({ repoId, featurePlanId } = {}) {
  const response = await api.get(`/repos/${repoId}/task-sources`, {
    params: featurePlanId ? { featurePlanId } : undefined,
  });
  return response.data.artifacts || [];
}

export async function getTaskSource({ repoId, artifactId }) {
  const response = await api.get(`/repos/${repoId}/task-sources/${artifactId}`);
  return response.data.artifact;
}

export async function retryTaskSource({ repoId, artifactId }) {
  const response = await api.post(`/repos/${repoId}/task-sources/${artifactId}/retry`);
  return response.data.artifact;
}

export async function reviewTaskSource({
  repoId,
  artifactId,
  observationKey,
  action,
  correctedText,
  explanation,
}) {
  const response = await api.post(`/repos/${repoId}/task-sources/${artifactId}/review`, {
    observationKey,
    action,
    correctedText,
    explanation,
  });
  return response.data.artifact;
}

export async function updateTaskSource({ repoId, artifactId, declaredRole, authoritative }) {
  const response = await api.patch(`/repos/${repoId}/task-sources/${artifactId}`, {
    declaredRole,
    authoritative,
  });
  return response.data.artifact;
}

export async function deleteTaskSource({ repoId, artifactId }) {
  await api.delete(`/repos/${repoId}/task-sources/${artifactId}`);
}

export async function fetchTaskSourcePreview({ repoId, artifactId }) {
  const response = await api.get(`/repos/${repoId}/task-sources/${artifactId}/content`, {
    responseType: "blob",
  });
  return URL.createObjectURL(response.data);
}

/**
 * Intent Contract lab helpers (Milestone 3 UI surface for M4–M6 APIs).
 * Extraction may call a provider — use a long timeout.
 */
const INTENT_CONTRACT_EXTRACT_TIMEOUT_MS = Math.max(
  LONG_TIMEOUT,
  QUERY_SEARCH_TIMEOUT_MS
);

export async function extractIntentContractDraft({
  repoId,
  productRequestText,
  featurePlanId,
  persist = true,
  runOmissionReview = false,
  sourceReferences,
} = {}) {
  const response = await api.post(
    "/intent-contracts/draft-extractions",
    {
      repoId,
      productRequestText,
      persist: persist === true,
      ...(featurePlanId ? { featurePlanId } : {}),
      ...(runOmissionReview ? { runOmissionReview: true } : {}),
      ...(Array.isArray(sourceReferences) ? { sourceReferences } : {}),
    },
    { timeout: INTENT_CONTRACT_EXTRACT_TIMEOUT_MS }
  );
  return response.data;
}

export async function getFeaturePlan(featurePlanId) {
  const response = await api.get(`/feature-plans/${featurePlanId}`);
  return response.data;
}

export async function getTaskProductDocument(featurePlanId) {
  const response = await api.get(`/feature-plans/${featurePlanId}/product-document`);
  return response.data;
}

export async function getExecutionContract(featurePlanId) {
  const response = await api.get(`/feature-plans/${featurePlanId}/execution-contract/preview`);
  return response.data;
}

export async function exportExecutionContract(featurePlanId, format = "json") {
  const response = await api.get(`/feature-plans/${featurePlanId}/execution-contract/export`, {
    params: { format },
    responseType: "text",
    transformResponse: [(data) => data],
  });
  return response.data;
}

export async function patchTaskProductDocument(featurePlanId, body) {
  const response = await api.patch(`/feature-plans/${featurePlanId}/product-document`, body);
  return response.data;
}

export async function createTaskProductProposal(featurePlanId, body) {
  const response = await api.post(`/feature-plans/${featurePlanId}/product-document/proposals`, body);
  return response.data;
}

export async function applyTaskProductProposal(featurePlanId, proposalId) {
  const response = await api.post(
    `/feature-plans/${featurePlanId}/product-document/proposals/${proposalId}/apply`,
    {}
  );
  return response.data;
}

export async function rejectTaskProductProposal(featurePlanId, proposalId) {
  const response = await api.post(
    `/feature-plans/${featurePlanId}/product-document/proposals/${proposalId}/reject`,
    {}
  );
  return response.data;
}

export async function undoTaskProductDocument(featurePlanId) {
  const response = await api.post(`/feature-plans/${featurePlanId}/product-document/undo`, {});
  return response.data;
}

export async function unfreezeTaskProductDocument(featurePlanId) {
  const response = await api.post(`/feature-plans/${featurePlanId}/product-document/unfreeze`, {});
  return response.data;
}

export async function approveTaskProductDocument(featurePlanId) {
  const response = await api.post(`/feature-plans/${featurePlanId}/product-document/approve`, {});
  return response.data;
}

export async function getIntentContractVersion(versionId) {
  const response = await api.get(`/intent-contracts/versions/${versionId}`);
  return response.data;
}

export async function getIntentContractVersionReadiness(versionId, policy = "strict") {
  const response = await api.get(`/intent-contracts/versions/${versionId}/readiness`, {
    params: { policy },
    timeout: DEFAULT_TIMEOUT,
  });
  return response.data;
}

export async function productApproveIntentContract(versionId, { note, policy } = {}) {
  const response = await api.post(
    `/intent-contracts/versions/${versionId}/product-approval`,
    {
      ...(typeof note === "string" ? { note } : {}),
      ...(policy ? { policy } : {}),
    },
    { timeout: DEFAULT_TIMEOUT }
  );
  return response.data;
}

export async function engineeringAcknowledgeIntentContract(versionId, { note } = {}) {
  const response = await api.post(
    `/intent-contracts/versions/${versionId}/engineering-acknowledgment`,
    {
      ...(typeof note === "string" ? { note } : {}),
    },
    { timeout: DEFAULT_TIMEOUT }
  );
  return response.data;
}

/** Engineer requests changes on a product-approved tip (does not mutate content). */
export async function engineeringRejectIntentContract(
  versionId,
  { reasonCode, comment } = {}
) {
  const response = await api.post(
    `/intent-contracts/versions/${versionId}/engineering-rejection`,
    {
      reasonCode,
      comment,
    },
    { timeout: DEFAULT_TIMEOUT }
  );
  return response.data;
}

/** Revise tip content (creates a new draft version). */
export async function reviseIntentContract(versionId, content, revision) {
  const response = await api.post(
    `/intent-contracts/versions/${versionId}/revisions`,
    {
      ...content,
      ...(revision ? { revision } : {}),
    },
    { timeout: DEFAULT_TIMEOUT }
  );
  return response.data;
}

/** Resolve/defer an unresolved question (M7; creates successor draft when content changes). */
export async function resolveIntentContractQuestion(
  versionId,
  questionId,
  { action, answerText, reason, note } = {}
) {
  const response = await api.post(
    `/intent-contracts/versions/${versionId}/questions/${encodeURIComponent(questionId)}/resolution`,
    {
      action,
      ...(typeof answerText === "string" ? { answerText } : {}),
      ...(typeof reason === "string" ? { reason } : {}),
      ...(typeof note === "string" ? { note } : {}),
    },
    { timeout: DEFAULT_TIMEOUT }
  );
  return response.data;
}

export async function fetchOrganizations() {
  const response = await api.get("/organizations");
  return response.data;
}

export async function fetchCurrentOrganization() {
  const response = await api.get("/organizations/current");
  return response.data;
}

export async function selectOrganization(organizationId) {
  const response = await api.post("/organizations/current", { organizationId });
  return response.data;
}

export async function fetchMembers(organizationId) {
  const response = await api.get(`/organizations/${organizationId}/members`);
  return response.data;
}

export async function fetchInvites(organizationId) {
  const response = await api.get(`/organizations/${organizationId}/invites`);
  return response.data;
}

export async function createInvite(organizationId, { email, role }) {
  const response = await api.post(`/organizations/${organizationId}/invites`, {
    email,
    role,
  });
  return response.data;
}

export async function resendInvite(organizationId, inviteId) {
  const response = await api.post(
    `/organizations/${organizationId}/invites/${inviteId}/resend`
  );
  return response.data;
}

export async function revokeInvite(organizationId, inviteId) {
  const response = await api.post(
    `/organizations/${organizationId}/invites/${inviteId}/revoke`
  );
  return response.data;
}

export async function updateMemberRole(organizationId, membershipId, role) {
  const response = await api.patch(
    `/organizations/${organizationId}/members/${membershipId}`,
    { role }
  );
  return response.data;
}

export async function removeMember(organizationId, membershipId) {
  const response = await api.delete(
    `/organizations/${organizationId}/members/${membershipId}`
  );
  return response.data;
}

export async function inspectInvite(token) {
  const response = await api.get("/organization-invites/inspect", {
    params: { token },
  });
  return response.data;
}

export async function acceptInvite(token) {
  const response = await api.post("/organization-invites/accept", { token });
  return response.data;
}

api.createPublicDemoSession = () => api.post("/public-demo/sessions");
api.sendPublicDemoMessage = (sessionId, data) => api.post(`/public-demo/sessions/${sessionId}/messages`, data);
api.getPublicDemoPreview = (sessionId) => api.get(`/public-demo/sessions/${sessionId}/preview`);
api.openPublicDemoReview = (sessionId) => api.post(`/public-demo/sessions/${sessionId}/review`);
api.createPublicDemoClaim = (sessionId) => api.post(`/public-demo/sessions/${sessionId}/claim-code`);
api.restartPublicDemo = (sessionId) => api.post(`/public-demo/sessions/${sessionId}/restart`);
api.importPublicDemo = (data) => api.post("/public-demo/import", data);

export default api;
export { normalizeError, DEFAULT_TIMEOUT, LONG_TIMEOUT, QUERY_SEARCH_TIMEOUT_MS };
